import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';

import { CreateLearningPathDto, LearningPath, PathLevel } from '../../../../core/interfaces/learning-path';
import { Subject, UpdateSubjectDto } from '../../../../core/interfaces/subject';
import { LearningService } from '../../../../core/services/learning-path.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { EmptyStateComponent } from '../../../ui/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import {
  CYCLE_LABEL_KEYS,
  CycleKey,
  PathDraft,
  SubjectDraft,
  groupByCycle,
  nextSubjectOrder,
  pathDraftError,
  pathDraftOf,
  sameDraft,
  sortSubjects,
  subjectDraftError,
  subjectDraftOf,
  toPathDto,
} from './learning-path.model';

/** La matière ouverte dans le dialogue : `id` nul = création. */
interface SubjectForm {
  id: number | null;
  pathId: number;
  draft: SubjectDraft;
}

/**
 * Parcours et matières, dans un seul écran.
 *
 * <p>Deux écrans les séparaient : la liste des parcours d'un côté, celle des
 * matières de l'autre, filtrée par un sélecteur de parcours. Or une matière
 * n'existe que dans son parcours — on la crée pour lui, on l'ordonne parmi les
 * siennes, on l'active pour le vendre. L'écran suit donc cette hiérarchie : les
 * parcours à gauche, groupés par cycle ; à droite, le parcours choisi et ses
 * matières.</p>
 *
 * <p>C'est ici que se décide ce qui se vend : une matière inactive n'apparaît
 * ni sur le site ni dans les listes de l'application. L'interrupteur
 * s'enregistre donc au clic, sans bouton à chercher — et se remet en place si
 * le serveur refuse.</p>
 */
@Component({
  selector: 'app-learning-path',
  standalone: true,
  imports: [FormsModule, NgTemplateOutlet, TranslateModule, EmptyStateComponent, ErrorStateComponent],
  templateUrl: './learning-path.component.html',
  // La mise en page maître–détail est celle de l'écran des packs : ses classes
  // sont reprises telles quelles plutôt que redessinées. La feuille locale ne
  // porte que ce que les packs n'ont pas — l'interrupteur et les dialogues.
  styleUrls: ['../packs/packs.component.css', './learning-path.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LearningPathComponent implements OnInit {
  private readonly learning = inject(LearningService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('pathDialog') private pathDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('subjectDialog') private subjectDialog?: ElementRef<HTMLDialogElement>;

  readonly cycleLabelKeys = CYCLE_LABEL_KEYS;
  readonly levels: readonly PathLevel[] = ['Primaire', 'College', 'Secondaire'];

  readonly paths = signal<LearningPath[]>([]);
  readonly loading = signal(true);
  readonly failed = signal(false);

  /** Recherche de parcours. Vide par défaut : elle ne filtre rien. */
  readonly search = signal('');
  readonly selectedId = signal<number | null>(null);

  readonly groups = computed(() => groupByCycle(this.paths(), this.search()));
  readonly selected = computed(
    () => this.paths().find((p) => p.id === this.selectedId()) ?? null,
  );
  readonly subjects = computed(() => sortSubjects(this.selected()?.subjects));

  /**
   * Les champs du parcours ouvert. Un signal écrit par `(ngModelChange)`, et
   * non des propriétés liées par `[(ngModel)]` : `dirty` en dépend, et un
   * `computed()` qui lirait des champs simples ne serait calculé qu'une fois.
   */
  readonly draft = signal<PathDraft>(pathDraftOf());
  readonly dirty = computed(() => {
    const path = this.selected();
    return path !== null && !sameDraft(this.draft(), pathDraftOf(path));
  });

  readonly savingPath = signal(false);

  /** Matières dont l'état est en cours d'enregistrement : leur interrupteur attend. */
  readonly toggling = signal<ReadonlySet<number>>(new Set());

  /** Le parcours en cours de création, tant que son dialogue est ouvert. */
  readonly newPath = signal<PathDraft | null>(null);
  /** La matière en cours de création ou de modification, tant que son dialogue est ouvert. */
  readonly subjectForm = signal<SubjectForm | null>(null);
  readonly savingDialog = signal(false);

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.failed.set(false);

    // Le référentiel entier : cet écran gère les matières, il lui faut aussi
    // celles qui sont inactives — partout ailleurs elles ne sont pas servies.
    this.learning
      .getReferential()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (res) => {
          this.paths.set(res?.data ?? []);
          // Le parcours ouvert le reste après un rechargement ; à défaut, le
          // premier de la liste telle qu'elle s'affiche.
          const kept = this.selected();
          this.open(kept ?? this.groups()[0]?.paths[0]?.path ?? null);
        },
        error: () => this.failed.set(true),
      });
  }

  // ── Parcours ────────────────────────────────────────────────────────────

  async select(path: LearningPath): Promise<void> {
    if (path.id === this.selectedId()) return;
    if (this.dirty() && !(await this.swal.confirmAsync(this.t('PATH_SUBJECTS.DISCARD')))) return;
    this.open(path);
  }

  private open(path: LearningPath | null): void {
    this.selectedId.set(path?.id ?? null);
    this.draft.set(pathDraftOf(path));
  }

  patchDraft(patch: Partial<PathDraft>): void {
    this.draft.update((d) => ({ ...d, ...patch }));
  }

  patchNewPath(patch: Partial<PathDraft>): void {
    this.newPath.update((d) => (d ? { ...d, ...patch } : d));
  }

  /** Route la saisie des champs partagés vers le parcours ouvert ou vers celui qu'on crée. */
  patchPath(target: 'current' | 'new', patch: Partial<PathDraft>): void {
    if (target === 'new') this.patchNewPath(patch);
    else this.patchDraft(patch);
  }

  savePath(): void {
    const path = this.selected();
    if (!path || this.savingPath()) return;

    const draft = this.draft();
    const error = pathDraftError(draft);
    if (error) {
      this.swal.error(this.t(error));
      return;
    }

    const dto = toPathDto(draft);
    this.savingPath.set(true);
    this.learning
      .updateLearningPath(path.id, { id: path.id, ...dto })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.savingPath.set(false)),
      )
      .subscribe({
        next: () => {
          this.paths.update((list) => list.map((p) => (p.id === path.id ? this.merged(p, dto) : p)));
          // La saisie a pu continuer pendant l'aller-retour : on ne recale le
          // formulaire que s'il montre encore ce qui vient de partir.
          if (this.selectedId() === path.id && sameDraft(this.draft(), draft)) {
            this.draft.set(pathDraftOf(this.merged(path, dto)));
          }
          this.swal.success(this.t('SWAL.UPDATED'));
        },
        error: (err) => this.swal.error(this.messageOf(err, 'PATH_SUBJECTS.ERROR_SAVE_PATH')),
      });
  }

  async deletePath(path: LearningPath): Promise<void> {
    const ok = await this.swal.confirmAsync(
      this.t('PATH_SUBJECTS.CONFIRM_DELETE_PATH', { title: path.title }),
    );
    if (!ok) return;

    this.learning
      .deleteLearningPath(path.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.paths.update((list) => list.filter((p) => p.id !== path.id));
          if (this.selectedId() === path.id) this.open(this.groups()[0]?.paths[0]?.path ?? null);
          this.swal.success(this.t('ADMIN.PATH_DELETED_SUCCESS'));
        },
        // Le serveur refuse un parcours dont une matière porte des tests : c'est
        // son message qui dit pourquoi.
        error: (err) => this.swal.error(this.messageOf(err, 'ADMIN.ERROR_DELETING_PATH')),
      });
  }

  openNewPath(): void {
    this.newPath.set(pathDraftOf());
    this.show(this.pathDialog);
  }

  createPath(): void {
    const draft = this.newPath();
    if (!draft || this.savingDialog()) return;

    const error = pathDraftError(draft);
    if (error) {
      this.swal.error(this.t(error));
      return;
    }

    this.savingDialog.set(true);
    this.learning
      .createLearningPath(toPathDto(draft))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.savingDialog.set(false)),
      )
      .subscribe({
        next: (res) => {
          const created: LearningPath = { ...res.data, subjects: [] };
          this.paths.update((list) => [...list, created]);
          this.closePathDialog();
          // Le nouveau parcours s'ouvre pour qu'on y ajoute ses matières —
          // sauf si l'on quitterait ainsi des modifications non enregistrées.
          if (!this.dirty()) this.open(created);
          this.swal.success(this.t('SWAL.CREATED'));
        },
        error: (err) => this.swal.error(this.messageOf(err, 'PATH_SUBJECTS.ERROR_SAVE_PATH')),
      });
  }

  // ── Matières ────────────────────────────────────────────────────────────

  /**
   * Active ou désactive une matière, tout de suite.
   *
   * <p>L'écran prend l'état demandé sans attendre, et le rend si le serveur
   * refuse : un interrupteur qui resterait basculé sur un échec mentirait sur
   * ce que le site vend. Seul l'état part — ni l'ordre, que le serveur
   * verrouille dès qu'une matière porte des tests, ni rien d'autre que cette
   * ligne n'a touché.</p>
   */
  toggleSubject(subject: Subject, isActive: boolean): void {
    if (this.toggling().has(subject.id) || subject.isActive === isActive) return;

    this.replaceSubject({ ...subject, isActive });
    this.setToggling(subject.id, true);

    this.learning
      .updateSubject(subject.id, { ...this.labelsOf(subject), isActive })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.setToggling(subject.id, false)),
      )
      .subscribe({
        next: () =>
          this.swal.success(
            this.t(isActive ? 'PATH_SUBJECTS.ACTIVATED' : 'PATH_SUBJECTS.DEACTIVATED', {
              name: subject.name,
            }),
          ),
        error: (err) => {
          this.patchSubject(subject.id, { isActive: subject.isActive });
          this.swal.error(this.messageOf(err, 'PATH_SUBJECTS.ERROR_TOGGLE'));
        },
      });
  }

  isToggling(subjectId: number): boolean {
    return this.toggling().has(subjectId);
  }

  openAddSubject(path: LearningPath): void {
    this.subjectForm.set({
      id: null,
      pathId: path.id,
      draft: subjectDraftOf(null, nextSubjectOrder(path.subjects)),
    });
    this.show(this.subjectDialog);
  }

  openEditSubject(subject: Subject): void {
    this.subjectForm.set({
      id: subject.id,
      pathId: subject.learningPathId,
      draft: subjectDraftOf(subject, 0),
    });
    this.show(this.subjectDialog);
  }

  patchSubjectDraft(patch: Partial<SubjectDraft>): void {
    this.subjectForm.update((f) => (f ? { ...f, draft: { ...f.draft, ...patch } } : f));
  }

  saveSubject(): void {
    const form = this.subjectForm();
    if (!form || this.savingDialog()) return;

    const error = subjectDraftError(form.draft);
    if (error) {
      this.swal.error(this.t(error));
      return;
    }

    const draft = form.draft;
    const labels = {
      name: draft.name.trim(),
      nameAr: draft.nameAr.trim() || null,
      nameFr: draft.nameFr.trim() || null,
      nameEn: draft.nameEn.trim() || null,
    };
    const displayOrder = draft.displayOrder ?? 0;

    this.savingDialog.set(true);
    const done = () => this.savingDialog.set(false);

    if (form.id === null) {
      this.learning
        .createSubject({ ...labels, displayOrder, isActive: draft.isActive, learningPathId: form.pathId })
        .pipe(takeUntilDestroyed(this.destroyRef), finalize(done))
        .subscribe({
          next: (res) => {
            this.addSubject({
              ...res.data,
              ...labels,
              displayOrder,
              isActive: draft.isActive,
              learningPathId: form.pathId,
            });
            this.closeSubjectDialog();
            this.swal.success(this.t('ADMIN.SUBJECT_CREATED_SUCCESS'));
          },
          error: (err) => this.swal.error(this.messageOf(err, 'ADMIN.ERROR_CREATING_SUBJECT')),
        });
      return;
    }

    const id = form.id;
    const before = this.findSubject(id);
    // L'ordre ne part que s'il change : le serveur refuse de réordonner une
    // matière qui porte des tests, et le lui renvoyer tel quel n'apporte rien.
    // Un ordre jamais renseigné s'affiche zéro : le retrouver à zéro n'est
    // pas un changement non plus.
    const reordered = (before?.displayOrder ?? 0) !== displayOrder;
    const dto: UpdateSubjectDto = {
      ...labels,
      isActive: draft.isActive,
      ...(reordered ? { displayOrder } : {}),
    };

    this.learning
      .updateSubject(id, dto)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(done))
      .subscribe({
        next: () => {
          this.patchSubject(id, { ...labels, isActive: draft.isActive, displayOrder });
          this.closeSubjectDialog();
          this.swal.success(this.t('ADMIN.SUBJECT_UPDATED_SUCCESS'));
        },
        error: (err) => this.swal.error(this.messageOf(err, 'ADMIN.ERROR_UPDATING_SUBJECT')),
      });
  }

  async deleteSubject(subject: Subject): Promise<void> {
    const ok = await this.swal.confirmAsync(
      this.t('PATH_SUBJECTS.CONFIRM_DELETE_SUBJECT', { name: subject.name }),
    );
    if (!ok) return;

    this.learning
      .deleteSubject(subject.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.updateSubjects(subject.learningPathId, (list) => list.filter((s) => s.id !== subject.id));
          this.swal.success(this.t('ADMIN.SUBJECT_DELETED_SUCCESS'));
        },
        // Une matière qui porte des tests ne se supprime pas : le serveur le
        // dit, et c'est sa phrase qu'on affiche. La désactiver reste possible.
        error: (err) => this.swal.error(this.messageOf(err, 'ADMIN.ERROR_DELETING_SUBJECT')),
      });
  }

  // ── Dialogues ───────────────────────────────────────────────────────────

  /**
   * Le formulaire se vide ici, sans attendre l'événement `close` du
   * dialogue : il est différé, d'un délai que le navigateur choisit. Le
   * gabarit l'écoute quand même — c'est lui qui vide le formulaire quand la
   * fermeture vient de la touche Échap.
   */
  closePathDialog(): void {
    this.close(this.pathDialog);
    this.newPath.set(null);
  }

  closeSubjectDialog(): void {
    this.close(this.subjectDialog);
    this.subjectForm.set(null);
  }

  /**
   * `<dialog>` + `showModal()` : peint dans la couche supérieure du navigateur,
   * hors de tout contexte d'empilement. La coquille de l'application casse
   * `position: fixed` — une modale maison s'y afficherait sous le contenu.
   */
  private show(dialog?: ElementRef<HTMLDialogElement>): void {
    const el = dialog?.nativeElement;
    if (el && !el.open) el.showModal();
  }

  private close(dialog?: ElementRef<HTMLDialogElement>): void {
    const el = dialog?.nativeElement;
    if (el?.open) el.close();
  }

  // ── État local ──────────────────────────────────────────────────────────

  private findSubject(id: number): Subject | undefined {
    for (const path of this.paths()) {
      const found = (path.subjects ?? []).find((s) => s.id === id);
      if (found) return found;
    }
    return undefined;
  }

  private updateSubjects(pathId: number, change: (subjects: Subject[]) => Subject[]): void {
    this.paths.update((list) =>
      list.map((p) => (p.id === pathId ? { ...p, subjects: change(p.subjects ?? []) } : p)),
    );
  }

  private addSubject(subject: Subject): void {
    this.updateSubjects(subject.learningPathId, (list) => [...list, subject]);
  }

  private replaceSubject(subject: Subject): void {
    this.updateSubjects(subject.learningPathId, (list) =>
      list.map((s) => (s.id === subject.id ? subject : s)),
    );
  }

  private patchSubject(id: number, patch: Partial<Subject>): void {
    const current = this.findSubject(id);
    if (current) this.replaceSubject({ ...current, ...patch });
  }

  private setToggling(id: number, on: boolean): void {
    this.toggling.update((set) => {
      const next = new Set(set);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  /** Le parcours tel qu'il vient d'être enregistré, ses matières intactes. */
  private merged(path: LearningPath, dto: CreateLearningPathDto): LearningPath {
    return { ...path, ...dto, durationHours: dto.durationHours as number };
  }

  /** Le nom et ses traductions : le serveur exige le nom à chaque modification. */
  private labelsOf(subject: Subject): Pick<UpdateSubjectDto, 'name' | 'nameAr' | 'nameFr' | 'nameEn'> {
    return {
      name: subject.name,
      nameAr: subject.nameAr ?? null,
      nameFr: subject.nameFr ?? null,
      nameEn: subject.nameEn ?? null,
    };
  }

  cycleLabelKey(cycle: CycleKey): string {
    return this.cycleLabelKeys[cycle];
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  /**
   * Le refus du serveur, dans la langue de l'utilisateur quand il porte un
   * code ; à défaut sa phrase, et en dernier recours un libellé de l'écran.
   */
  private messageOf(err: unknown, fallbackKey: string): string {
    const body = (err as { error?: { code?: string; message?: string } })?.error;

    if (body?.code) {
      const key = `PATH_SUBJECTS.ERRORS.${body.code}`;
      const translated = this.t(key);
      // ngx-translate rend la clé telle quelle quand elle est absente.
      if (translated !== key) return translated;
    }

    return body?.message || this.t(fallbackKey);
  }
}
